import { Component, OnInit, InjectionToken, Inject } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { ReportService, ReportSasResponse, ProblemDetails } from "../../../shared/services/report.service";
import { MessageModule } from "primeng/message";
import { CONSTANTS } from "../../../constants";
import { LoggerService } from "../../../shared/services/logger.service";

export const WINDOW = new InjectionToken<Window>("Window", {
  providedIn: "root",
  factory: () => window,
});

@Component({
  selector: "app-report-viewer",
  standalone: true,
  imports: [MessageModule],
  providers: [DatePipe],
  templateUrl: "./report-viewer.component.html",
  styleUrl: "./report-viewer.component.scss",
})
export class ReportViewerComponent implements OnInit {
  isLoading = true;
  errorMessage = "";
  errorSeverity = "error";

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportService,
    private datePipe: DatePipe,
    private logger: LoggerService,
    @Inject(WINDOW) private window: Window,
  ) { }

  ngOnInit(): void {
    const externalLotIdString = this.route.snapshot.paramMap.get("externalLotId");
    const externalLotId = Number(externalLotIdString);
    const reportStep = this.route.snapshot.queryParamMap.get("reportStep");

    if (!externalLotId || !reportStep) {
      this.errorMessage = "Invalid report link.";
      this.isLoading = false;
      return;
    }

    this.reportService.getReportSasUrl(externalLotId, reportStep).subscribe({
      next: (response: ReportSasResponse) => {
        if (response.reportSasUrl) {
          this.window.location.href = response.reportSasUrl;
        } else {
          this.errorMessage = "Report URL not found.";
          this.errorSeverity = "error";
          this.isLoading = false;
        }
      },
      error: (err: HttpErrorResponse) => {
        this.logger.error("Error fetching report URL", err);
        this.isLoading = false;

        const problem = err.error as ProblemDetails;

        if (problem && problem.title) {
          this.errorMessage = problem.detail || "An unexpected error occurred.";

          switch (err.status) {
            case 404:
            case 400:
              this.errorSeverity = "warn";
              break;
            case 503:
              this.errorSeverity = "info";

              if (problem.retryAtUtc) {
                const localDateTime = this.datePipe.transform(problem.retryAtUtc, "MM/dd/yyyy h:mm a");
                this.errorMessage = `${this.errorMessage} Please check back after ${localDateTime}.`;
              }
              break;

            case 500:
            default:
              this.errorSeverity = "error";
              break;
          }
        } else {
          if (err.status === CONSTANTS.HTTP_STATUS_CODES.NOT_FOUND) {
            this.errorMessage = "This report is not currently available in BQA.";
            this.errorSeverity = "warn";
          } else {
            this.errorMessage = "We're unable to retrieve this report due to a technical issue.";
            this.errorSeverity = "error";
          }
        }
      },
    });
  }
}
